import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
	ArrowLeft,
	MapPin,
	Package,
	Gift,
	Truck,
	CreditCard,
	Banknote,
	Loader2,
	Check,
	X,
	Plus,
	ChevronDown,
	AlertTriangle,
	ImageOff,
	Save,
} from 'lucide-react'
import Layout from '@/components/layout/layout'
import Toast from '@/components/elements/toast'
import AuthRequiredScreen from '@/components/default/auth-required'
import { ToastConfirm } from '@/components/elements/toast-confirm'
import { AppDispatch, RootState } from '@/store/store'
import CustomersEntity, {
	CustomersAddressEntity,
} from '@/entities/api/customers/customer'
import CartEntity from '@/entities/api/cart/cart'
import CouponsEntity, { TypesCoupon } from '@/entities/api/coupons/coupon'
import useCartCalculations from '@/hooks/useCartCalculations'
import { getCouponByCode } from '@/services/api/coupons/coupons'
import formatCurrency from '@/utils/price-convert'
import {
	attributeMaps,
	colorsMap,
	sizeMap,
	flavorMap,
	objectivesMap,
	modalitiesMap,
	NameAttributes,
} from '@/entities/api/products/product'
import { createOrder } from '@/services/api/order/order'
import { clearOrdersFn } from '@/store/features/orders'
import { OrderPaymethods } from '@/entities/api/order/order-stacks'
import { API_URL } from '@/services/api/keys'
import { removeFromCart } from '@/store/features/cart'
import { deleteCartProductById } from '@/services/api/cart/cart'
import Select from '@/components/elements/select'
import addressSchema from '@/entities/schemas/address'
import * as yup from 'yup'
import municipiosData from '@/utils/muncipios.json'
import { createCustomerAddress } from '@/services/api/customers/customer'
import { updateUserData } from '@/store/features/auth'
import InputGroupContent from '@/components/elements/input'
type AddressFormData = yup.InferType<typeof addressSchema>

const PaymentScreen = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const orders = useSelector((state: RootState) => state.orders.orders)
	const { cart } = useSelector((state: RootState) => state.cart)

	const [selectedAddress, setSelectedAddress] =
		useState<CustomersAddressEntity | null>(null)
	const [isAddressExpanded, setIsAddressExpanded] = useState(false)
	const [showAddressForm, setShowAddressForm] = useState(false)
	const [isSubmittingAddress, setIsSubmittingAddress] = useState(false)
	const [promoCode, setPromoCode] = useState('')
	const [coupon, setCoupon] = useState<Partial<CouponsEntity> | null>(
		orders?.coupon || null
	)
	const [isCouponLoading, setIsCouponLoading] = useState(false)
	const [isProcessing, setIsProcessing] = useState(false)
	const [selectedPaymentMethod, setSelectedPaymentMethod] =
		useState<OrderPaymethods>(OrderPaymethods.payment_on_delivery)

	const items: CartEntity[] = orders?.items || cart
	const existingCoupon = orders?.coupon || coupon

	const {
		register: addressRegister,
		handleSubmit: handleAddressSubmit,
		formState: { errors: addressErrors },
		reset: addressReset,
		setValue: setAddressValue,
		watch: watchAddress,
	} = useForm<AddressFormData>({
		resolver: yupResolver(addressSchema),
		defaultValues: {
			fullname: '',
			document_type: 'CC',
			document: 0,
			province: '',
			city: '',
			neighborhood: '',
			street_type: 'Calle',
			street: '',
			number_home: '',
			type_home: 'Casa',
			phone: 0,
			additional_references: '',
			postal_code: '',
		},
	})

	const watchProvince = watchAddress('province')

	const departmentOptions = useMemo(() => {
		return municipiosData.departamentos.map((dept) => ({
			label: dept.nombre,
			value: dept.nombre,
		}))
	}, [])

	const cityOptions = useMemo(() => {
		if (!watchProvince) return []

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		if (!selectedDept) return []

		if (!selectedDept.municipios || selectedDept.municipios.length === 0) {
			return [
				{
					label: selectedDept.nombre,
					value: selectedDept.nombre,
				},
			]
		}

		return selectedDept.municipios.map((mun) => ({
			label: mun.nombre,
			value: mun.nombre,
		}))
	}, [watchProvince])

	const isDepartmentWithoutMunicipios = useMemo(() => {
		if (!watchProvince) return false

		const selectedDept = municipiosData.departamentos.find(
			(dept) => dept.nombre === watchProvince
		)

		return (
			selectedDept &&
			(!selectedDept.municipios || selectedDept.municipios.length === 0)
		)
	}, [watchProvince])

	useEffect(() => {
		if (isDepartmentWithoutMunicipios && watchProvince) {
			setAddressValue('city', watchProvince)
		} else if (watchProvince) {
			setAddressValue('city', '')
		}
	}, [watchProvince, isDepartmentWithoutMunicipios, setAddressValue])

	const calculatedData = useCartCalculations({
		items: items,
		coupon: existingCoupon,
	})

	const finalData = {
		totalItems: orders?.totalItems || calculatedData.totalItems,
		totalPrice: orders?.totalPrice || calculatedData.totalPrice,
		totalShipping: orders?.totalShipping || calculatedData.totalShipping,
		couponDiscount: orders?.couponDiscount || calculatedData.couponDiscount,
		subtotalAfterCoupon:
			orders?.subtotalAfterCoupon || calculatedData.subtotalAfterCoupon,
		grandTotal: orders?.grandTotal || calculatedData.grandTotal,
	}

	const calculateItemTotal = (item: CartEntity): number => {
		const attribute = item.product.attributes?.[0]
		if (!attribute) return 0

		const price = attribute.price || 0
		const quantity = attribute.quantity || 0
		const discount = item.product.discount || 0

		const discountedPrice = price - (price * discount) / 100
		return discountedPrice * quantity
	}

	const calculateItemShipping = (item: CartEntity): number => {
		if (!item.product.shipping_info) return 0

		const shippingInfo = item.product.shipping_info
		const attribute = item.product.attributes?.[0]
		const quantity = attribute?.quantity || 0

		if (
			!shippingInfo.isHaveShipping ||
			shippingInfo.type === 'free' ||
			quantity === 0
		) {
			return 0
		}

		if (shippingInfo.type === 'pay') {
			const baseShippingPrice = shippingInfo.price || 0
			const totalAttributes =
				item.product.attributes?.reduce((acc, attr) => {
					return acc + (attr.quantity || 0)
				}, 0) || 0

			let totalShipping = baseShippingPrice

			if (totalAttributes > 1) {
				const additionalAttributes = totalAttributes - 1
				totalShipping += baseShippingPrice * 0.5 * additionalAttributes
			}

			return totalShipping
		}

		return 0
	}

	useEffect(() => {
		if (customer?.address && customer.address.length > 0) {
			const validAddresses = customer.address.filter((addr) => !addr._deleted)
			if (validAddresses.length > 0) {
				setSelectedAddress(validAddresses[0])
			}
		}
	}, [customer])

	useEffect(() => {
		if (orders?.coupon) {
			setCoupon(orders.coupon)
		}
	}, [orders])

	const isCouponValid = (coupon: Partial<CouponsEntity>): boolean => {
		if (!coupon.expiration) return false
		const expirationDate = new Date(coupon.expiration)
		const currentDate = new Date()
		return expirationDate > currentDate
	}

	const getAttributeDisplayValue = (attribute: any): string => {
		try {
			const values = Array.isArray(attribute.value)
				? attribute.value
				: [attribute.value]
			const names = Array.isArray(attribute.name)
				? attribute.name
				: [attribute.name]

			if (values.length === 0) return ''

			const displayValues = values.map((value: any, index: number) => {
				const name = names[index] || names[0]
				const valueStr = String(value)

				switch (name) {
					case NameAttributes.COLOR:
						return colorsMap[valueStr as keyof typeof colorsMap]
							? valueStr
							: valueStr
					case NameAttributes.SIZE:
						return sizeMap[valueStr as keyof typeof sizeMap]
							? `${valueStr} (${sizeMap[valueStr as keyof typeof sizeMap]})`
							: valueStr
					case NameAttributes.FLAVOR:
						return flavorMap[valueStr as keyof typeof flavorMap] || valueStr
					case NameAttributes.OBJECTIVE:
						return (
							objectivesMap[valueStr as keyof typeof objectivesMap] || valueStr
						)
					case NameAttributes.MODALITY:
						return (
							modalitiesMap[valueStr as keyof typeof modalitiesMap] || valueStr
						)
					default:
						return (
							attributeMaps[valueStr as keyof typeof attributeMaps] || valueStr
						)
				}
			})

			return displayValues.join(' / ')
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return (
				attributeMaps[String(fallbackValue) as keyof typeof attributeMaps] ||
				String(fallbackValue || '')
			)
		}
	}

	const handleSelectAddress = (address: CustomersAddressEntity) => {
		setSelectedAddress(address)
		Toast({
			type: 'success',
			message: `Dirección de ${address.fullname} seleccionada`,
		})
	}

	const handleApplyPromoCode = async () => {
		if (!promoCode.trim()) {
			Toast({ type: 'warning', message: 'Ingresa un código de cupón' })
			return
		}

		setIsCouponLoading(true)
		try {
			const response = await getCouponByCode(promoCode, token ?? '')

			if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
				Toast({ type: 'error', message: 'Cupón no encontrado o inválido' })
				setIsCouponLoading(false)
				return
			}

			const couponData = response?.data

			if (!isCouponValid(couponData)) {
				Toast({ type: 'error', message: 'El cupón ha expirado' })
				setIsCouponLoading(false)
				return
			}

			setCoupon(couponData)
			Toast({ type: 'success', message: 'Cupón aplicado exitosamente' })
		} catch (error) {
			Toast({ type: 'error', message: 'Error al aplicar el cupón' })
		} finally {
			setIsCouponLoading(false)
		}
	}

	const handleRemoveCoupon = () => {
		setCoupon(null)
		setPromoCode('')
		Toast({ type: 'info', message: 'Cupón removido' })
	}

	const handleCreateOrder = async () => {
		if (!selectedAddress) {
			Toast({ type: 'warning', message: 'Selecciona una dirección de entrega' })
			return
		}

		if (!selectedAddress._id) {
			Toast({
				type: 'error',
				message: 'La dirección seleccionada no es válida',
			})
			return
		}

		if (items.length === 0) {
			Toast({ type: 'warning', message: 'No hay productos en tu pedido' })
			return
		}

		const selectedAddressName = selectedAddress.fullname
		const selectedAddressLocation = `${selectedAddress.city}, ${selectedAddress.neighborhood}`

		ToastConfirm({
			message: `Al confirmar tu pedido te comprometes a recibirlo en la dirección de ${selectedAddressName} (${selectedAddressLocation}). El incumplimiento puede tener consecuencias legales y será reportado. ¿Deseas continuar?`,
			onConfirm: async () => {
				setIsProcessing(true)

				try {
					const orderData = {
						customer: {
							customerId: customer?._id || '',
							addressId: selectedAddress._id,
						},
						coupon: coupon?._id || undefined,
						payment_info: {
							paymethod:
								selectedPaymentMethod === OrderPaymethods.payment_on_delivery
									? OrderPaymethods.payment_on_delivery
									: selectedPaymentMethod,
						},
						products: (() => {
							const productMap = new Map()

							items.forEach((item) => {
								const productId = item.product._id
								if (!productId) return

								if (!productMap.has(productId)) {
									productMap.set(productId, {
										_id: productId,
										attributes: [],
									})
								}

								const productData = productMap.get(productId)

								;(item.product.attributes || []).forEach((attr) => {
									if (!attr?._id) return

									const exists = productData.attributes.find(
										(a: any) => a._id === attr._id
									)

									if (!exists) {
										productData.attributes.push({
											_id: attr._id,
											quantity: attr.quantity || 0,
										})
									} else {
										exists.quantity += attr.quantity || 0
									}
								})
							})

							return Array.from(productMap.values())
						})(),
					}

					const response = await createOrder(orderData, token ?? '')

					if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
						Toast({
							type: 'error',
							message: response?.data?.message || 'Error al crear la orden',
						})
						setIsProcessing(false)
						return
					}

					const cartItems = orders?.items

					if ((cartItems?.length ?? 0) >= 1) {
						cartItems?.map(async (item) => {
							await deleteCartProductById(item?._id || '', token ?? '')
							dispatch(removeFromCart({ cartId: item?._id || '' }))
						})
					}

					Toast({ type: 'success', message: 'Pedido creado exitosamente' })
					dispatch(clearOrdersFn())
					navigate('/Perfil/compras')
				} catch (error) {
					Toast({ type: 'error', message: 'Error al procesar el pedido' })
				} finally {
					setIsProcessing(false)
				}
			},
			onCancel: () => {
				Toast({ type: 'info', message: 'Pedido cancelado' })
			},
		})
	}

	const handleAddAddress = () => {
		setShowAddressForm(true)
		addressReset({
			fullname: '',
			document_type: 'CC',
			document: 0,
			province: '',
			city: '',
			neighborhood: '',
			street_type: 'Calle',
			street: '',
			number_home: '',
			type_home: 'Casa',
			phone: 0,
			additional_references: '',
			postal_code: '',
		})
	}

	const handleCancelAddressForm = () => {
		setShowAddressForm(false)
		addressReset()
	}

	const onAddressSubmit = async (data: AddressFormData) => {
		if (!customer?._id || !token) {
			Toast({ type: 'error', message: 'Datos de sesión no disponibles' })
			return
		}

		try {
			setIsSubmittingAddress(true)

			if (customer?.address && customer?.address.length >= 5) {
				Toast({
					type: 'error',
					message: 'Solo se pueden agregar 5 direcciones por usuario',
				})
				setIsSubmittingAddress(false)
				return
			}

			const response = await createCustomerAddress(customer._id, data, token)

			if (response?.status === 200 || response?.status === 201) {
				if (response?.data?.data) {
					const newAddress = response.data.data
					const existingAddresses = customer.address || []
					const updatedAddresses = [...existingAddresses, newAddress]

					dispatch(
						updateUserData({
							customer: {
								...customer,
								address: updatedAddresses,
							},
						})
					)

					setSelectedAddress(newAddress)
					Toast({ type: 'success', message: 'Dirección creada exitosamente' })
					setShowAddressForm(false)
					addressReset()
				}
			} else {
				throw new Error(
					response?.data?.message || 'Error al crear la dirección'
				)
			}
		} catch (error) {
			console.error('Error al crear dirección:', error)
			Toast({
				type: 'error',
				message: 'Error al crear la dirección. Inténtalo nuevamente.',
			})
		} finally {
			setIsSubmittingAddress(false)
		}
	}

	const getValidAddresses = () => {
		return customer?.address?.filter((address) => !address._deleted) || []
	}

	const validAddresses = getValidAddresses()

	if (!customer || !token) {
		return (
			<Layout customer={null} token={null}>
				<AuthRequiredScreen />
			</Layout>
		)
	}

	if (items.length === 0) {
		return (
			<Layout customer={customer} token={token}>
				<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
					<div className='text-center'>
						<Package className='w-16 h-16 text-gray-400 mx-auto mb-4' />
						<h2 className='text-xl font-bold text-gray-900 mb-2'>
							No hay productos para pagar
						</h2>
						<p className='text-gray-600 mb-4'>
							Agrega productos a tu carrito para continuar
						</p>
						<button
							onClick={() => navigate('/')}
							className='bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-colors'
						>
							Ir a comprar
						</button>
					</div>
				</div>
			</Layout>
		)
	}

	return (
		<Layout customer={customer} token={token}>
			<div className='bg-gradient-to-b from-gray-100 via-gray-50 to-white min-h-screen py-[4vh] px-[2vw] lg:px-[6vw]'>
				<div className='max-w-full mx-auto'>
					<div className='flex items-center gap-3 mb-8'>
						<button
							onClick={() => navigate(-1)}
							className='p-2 hover:bg-white rounded-xl transition-all'
						>
							<ArrowLeft className='w-5 h-5 text-gray-600' />
						</button>
						<div>
							<h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
								Finalizar Compra
							</h1>
							<p className='text-gray-600 text-sm'>
								{finalData.totalItems} productos
							</p>
						</div>
					</div>

					<div className='bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6'>
						<div className='flex items-start gap-3'>
							<AlertTriangle className='w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5' />
							<div>
								<h3 className='font-semibold text-amber-900 mb-1'>
									Compromiso de Entrega
								</h3>
								<p className='text-sm text-amber-800'>
									Al realizar este pedido te comprometes a recibirlo en la
									dirección indicada. El incumplimiento puede tener
									consecuencias legales y será reportado a las autoridades
									competentes.
								</p>
							</div>
						</div>
					</div>

					<div className='grid grid-cols-1 lg:grid-cols-3 gap-8'>
						<div className='lg:col-span-2 space-y-6'>
							<div className='bg-white rounded-3xl p-6 shadow-sm'>
								<div className='flex items-center gap-3 mb-6'>
									<div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center'>
										<MapPin className='w-5 h-5 text-red-600' />
									</div>
									<div>
										<h2 className='text-xl font-bold text-gray-900'>
											Dirección de Entrega
										</h2>
										<p className='text-gray-600 text-sm'>
											Selecciona dónde quieres recibir tu pedido
										</p>
									</div>
								</div>

								{validAddresses.length > 0 ? (
									<div className='space-y-4'>
										{!showAddressForm ? (
											<>
												<div className='border border-gray-200 rounded-2xl overflow-hidden'>
													{validAddresses
														.slice(
															0,
															isAddressExpanded ? validAddresses.length : 1
														)
														.map((address, index) => (
															<div
																key={address._id || index}
																className={`p-4 transition-all border-b border-gray-100 last:border-b-0 cursor-pointer ${
																	selectedAddress?._id === address._id
																		? 'bg-red-50 border-l-4 border-l-red-600'
																		: 'hover:bg-gray-50'
																}`}
																onClick={() => handleSelectAddress(address)}
															>
																<div className='flex items-start justify-between'>
																	<div className='flex-1'>
																		<div className='flex items-center gap-3 mb-2'>
																			<div
																				className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
																					selectedAddress?._id === address._id
																						? 'bg-red-600 border-red-600'
																						: 'border-gray-300'
																				}`}
																			>
																				{selectedAddress?._id ===
																					address._id && (
																					<Check className='w-2 h-2 text-white' />
																				)}
																			</div>
																			<h3 className='font-semibold text-gray-900'>
																				{address.fullname}
																			</h3>
																		</div>
																		<p className='text-sm text-gray-600 ml-7'>
																			{address.document_type}:{' '}
																			{address.document}
																		</p>
																		<p className='text-sm text-gray-600 ml-7'>
																			{address.street_type} {address.street} #
																			{address.number_home}
																		</p>
																		<p className='text-sm text-gray-600 ml-7'>
																			{address.neighborhood}, {address.city},{' '}
																			{address.province}
																		</p>
																		<p className='text-sm text-gray-500 ml-7'>
																			Tel: {address.phone}
																		</p>
																		{address.additional_references && (
																			<p className='text-sm text-gray-500 ml-7'>
																				Ref: {address.additional_references}
																			</p>
																		)}
																	</div>
																</div>
															</div>
														))}
												</div>

												{validAddresses.length > 1 && (
													<button
														onClick={() =>
															setIsAddressExpanded(!isAddressExpanded)
														}
														className='w-full flex items-center justify-center gap-2 py-3 text-red-600 hover:text-red-700 font-medium transition-colors'
													>
														<span>
															{isAddressExpanded
																? 'Ver menos'
																: `Ver todas (${validAddresses.length})`}
														</span>
														<ChevronDown
															className={`w-4 h-4 transition-transform ${
																isAddressExpanded ? 'rotate-180' : ''
															}`}
														/>
													</button>
												)}

												<button
													onClick={handleAddAddress}
													className='w-full flex items-center justify-center gap-2 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors border border-gray-200 rounded-xl hover:bg-gray-50'
												>
													<Plus className='w-4 h-4' />
													Agregar nueva dirección
												</button>
											</>
										) : (
											<div className='space-y-6'>
												<form
													onSubmit={handleAddressSubmit(onAddressSubmit)}
													className='space-y-6'
												>
													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 pb-2'>
															Nueva Dirección
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<InputGroupContent
																label='Nombre completo'
																placeholder='Nombre para entregas'
																type='text'
																name='fullname'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
															<Select
																name='document_type'
																label='Tipo de documento'
																placeholder='Selecciona el tipo de documento'
																options={[
																	{
																		label: 'Cédula de Ciudadanía',
																		value: 'CC',
																	},
																	{
																		label: 'Cédula de Extranjería',
																		value: 'CE',
																	},
																	{ label: 'NIT', value: 'NIT' },
																	{ label: 'Pasaporte', value: 'PP' },
																]}
																defaultValue={{
																	label: 'Cédula de Ciudadanía',
																	value: 'CC',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
														</div>
														<InputGroupContent
															label='Número de documento'
															placeholder='12345678'
															type='number'
															name='document'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>
													</div>

													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
															Ubicación
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<Select
																name='province'
																label='Departamento'
																placeholder='Selecciona el departamento'
																options={departmentOptions}
																defaultValue={{
																	label: '',
																	value: '',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<Select
																name='city'
																label='Ciudad'
																placeholder={
																	watchProvince
																		? 'Selecciona la ciudad'
																		: 'Primero selecciona un departamento'
																}
																options={cityOptions}
																defaultValue={{
																	label: '',
																	value: '',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
																isDisabled={
																	!watchProvince ||
																	isDepartmentWithoutMunicipios
																}
															/>
														</div>
														<InputGroupContent
															label='Barrio'
															placeholder='Nombre del barrio'
															type='text'
															name='neighborhood'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>
													</div>

													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
															Dirección Detallada
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
															<Select
																name='street_type'
																label='Tipo de vía'
																placeholder='Selecciona el tipo de vía'
																options={[
																	{ label: 'Calle', value: 'Calle' },
																	{ label: 'Carrera', value: 'Carrera' },
																	{ label: 'Avenida', value: 'Avenida' },
																	{ label: 'Diagonal', value: 'Diagonal' },
																	{
																		label: 'Transversal',
																		value: 'Transversal',
																	},
																]}
																defaultValue={{
																	label: 'Calle',
																	value: 'Calle',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<InputGroupContent
																label='Nombre de vía'
																placeholder='Ej: 15'
																type='text'
																name='street'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
															<InputGroupContent
																label='Número'
																placeholder='Ej: 25-10'
																type='text'
																name='number_home'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
														</div>

														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<Select
																name='type_home'
																label='Tipo de vivienda'
																placeholder='Selecciona el tipo de vivienda'
																options={[
																	{ label: 'Casa', value: 'Casa' },
																	{
																		label: 'Apartamento',
																		value: 'Apartamento',
																	},
																	{ label: 'Oficina', value: 'Oficina' },
																	{ label: 'Local Comercial', value: 'Local' },
																]}
																defaultValue={{
																	label: 'Casa',
																	value: 'Casa',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<InputGroupContent
																label='Teléfono'
																placeholder='Teléfono de contacto'
																type='number'
																name='phone'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
														</div>

														<InputGroupContent
															label='Código postal'
															placeholder='Ej: 660003'
															type='text'
															name='postal_code'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>

														<InputGroupContent
															label='Referencias adicionales (opcional)'
															placeholder='Casa blanca, portón negro, frente al parque...'
															type='text'
															name='additional_references'
															register={addressRegister}
															errors={addressErrors}
															required={false}
														/>
													</div>

													<div className='flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200'>
														<button
															type='submit'
															disabled={isSubmittingAddress}
															className='inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
														>
															{isSubmittingAddress ? (
																<>
																	<div className='w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin' />
																	Guardando...
																</>
															) : (
																<>
																	<Save className='w-4 h-4 mr-2' />
																	Crear dirección
																</>
															)}
														</button>
														<button
															type='button'
															onClick={handleCancelAddressForm}
															disabled={isSubmittingAddress}
															className='inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
														>
															<X className='w-4 h-4 mr-2' />
															Cancelar
														</button>
													</div>
												</form>
											</div>
										)}
									</div>
								) : (
									<div className='text-center py-8'>
										{!showAddressForm ? (
											<>
												<MapPin className='w-12 h-12 text-gray-300 mx-auto mb-4' />
												<h3 className='font-semibold text-gray-900 mb-2'>
													No tienes direcciones guardadas
												</h3>
												<p className='text-gray-600 text-sm mb-4'>
													Agrega una dirección para continuar con tu compra
												</p>
												<button
													onClick={handleAddAddress}
													className='bg-red-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-red-700 transition-colors'
												>
													<Plus className='w-4 h-4 inline mr-2' />
													Agregar Dirección
												</button>
											</>
										) : (
											<div className='space-y-6 text-left'>
												<form
													onSubmit={handleAddressSubmit(onAddressSubmit)}
													className='space-y-6'
												>
													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 pb-2'>
															Nueva Dirección
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<InputGroupContent
																label='Nombre completo'
																placeholder='Nombre para entregas'
																type='text'
																name='fullname'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
															<Select
																name='document_type'
																label='Tipo de documento'
																placeholder='Selecciona el tipo de documento'
																options={[
																	{
																		label: 'Cédula de Ciudadanía',
																		value: 'CC',
																	},
																	{
																		label: 'Cédula de Extranjería',
																		value: 'CE',
																	},
																	{ label: 'NIT', value: 'NIT' },
																	{ label: 'Pasaporte', value: 'PP' },
																]}
																defaultValue={{
																	label: 'Cédula de Ciudadanía',
																	value: 'CC',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
														</div>
														<InputGroupContent
															label='Número de documento'
															placeholder='12345678'
															type='number'
															name='document'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>
													</div>

													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
															Ubicación
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<Select
																name='province'
																label='Departamento'
																placeholder='Selecciona el departamento'
																options={departmentOptions}
																defaultValue={{
																	label: '',
																	value: '',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<Select
																name='city'
																label='Ciudad'
																placeholder={
																	watchProvince
																		? 'Selecciona la ciudad'
																		: 'Primero selecciona un departamento'
																}
																options={cityOptions}
																defaultValue={{
																	label: '',
																	value: '',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
																isDisabled={
																	!watchProvince ||
																	isDepartmentWithoutMunicipios
																}
															/>
														</div>
														<InputGroupContent
															label='Barrio'
															placeholder='Nombre del barrio'
															type='text'
															name='neighborhood'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>
													</div>

													<div className='space-y-4'>
														<h3 className='text-lg font-medium text-gray-900 border-b border-gray-200 pb-2'>
															Dirección Detallada
														</h3>
														<div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
															<Select
																name='street_type'
																label='Tipo de vía'
																placeholder='Selecciona el tipo de vía'
																options={[
																	{ label: 'Calle', value: 'Calle' },
																	{ label: 'Carrera', value: 'Carrera' },
																	{ label: 'Avenida', value: 'Avenida' },
																	{ label: 'Diagonal', value: 'Diagonal' },
																	{
																		label: 'Transversal',
																		value: 'Transversal',
																	},
																]}
																defaultValue={{
																	label: 'Calle',
																	value: 'Calle',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<InputGroupContent
																label='Nombre de vía'
																placeholder='Ej: 15'
																type='text'
																name='street'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
															<InputGroupContent
																label='Número'
																placeholder='Ej: 25-10'
																type='text'
																name='number_home'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
														</div>

														<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
															<Select
																name='type_home'
																label='Tipo de vivienda'
																placeholder='Selecciona el tipo de vivienda'
																options={[
																	{ label: 'Casa', value: 'Casa' },
																	{
																		label: 'Apartamento',
																		value: 'Apartamento',
																	},
																	{ label: 'Oficina', value: 'Oficina' },
																	{ label: 'Local Comercial', value: 'Local' },
																]}
																defaultValue={{
																	label: 'Casa',
																	value: 'Casa',
																}}
																register={addressRegister}
																errors={addressErrors}
																isRequired={true}
															/>
															<InputGroupContent
																label='Teléfono'
																placeholder='Teléfono de contacto'
																type='number'
																name='phone'
																register={addressRegister}
																errors={addressErrors}
																required={true}
															/>
														</div>

														<InputGroupContent
															label='Código postal'
															placeholder='Ej: 660003'
															type='text'
															name='postal_code'
															register={addressRegister}
															errors={addressErrors}
															required={true}
														/>

														<InputGroupContent
															label='Referencias adicionales (opcional)'
															placeholder='Casa blanca, portón negro, frente al parque...'
															type='text'
															name='additional_references'
															register={addressRegister}
															errors={addressErrors}
															required={false}
														/>
													</div>

													<div className='flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200'>
														<button
															type='submit'
															disabled={isSubmittingAddress}
															className='inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100'
														>
															{isSubmittingAddress ? (
																<>
																	<div className='w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin' />
																	Guardando...
																</>
															) : (
																<>
																	<Save className='w-4 h-4 mr-2' />
																	Crear dirección
																</>
															)}
														</button>
														<button
															type='button'
															onClick={handleCancelAddressForm}
															disabled={isSubmittingAddress}
															className='inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed'
														>
															<X className='w-4 h-4 mr-2' />
															Cancelar
														</button>
													</div>
												</form>
											</div>
										)}
									</div>
								)}
							</div>

							<div className='bg-white rounded-3xl p-6 shadow-sm'>
								<div className='flex items-center gap-3 mb-6'>
									<div className='w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center'>
										<CreditCard className='w-5 h-5 text-blue-600' />
									</div>
									<div>
										<h2 className='text-xl font-bold text-gray-900'>
											Método de Pago
										</h2>
										<p className='text-gray-600 text-sm'>
											Elige cómo quieres pagar tu pedido
										</p>
									</div>
								</div>

								<div className='space-y-4'>
									<div
										onClick={() =>
											setSelectedPaymentMethod(
												OrderPaymethods.payment_on_delivery
											)
										}
										className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
											selectedPaymentMethod ===
											OrderPaymethods.payment_on_delivery
												? 'border-red-600 bg-red-50'
												: 'border-gray-200 hover:border-gray-300'
										}`}
									>
										<div className='flex items-center gap-3'>
											<div
												className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
													selectedPaymentMethod ===
													OrderPaymethods.payment_on_delivery
														? 'bg-red-600 border-red-600'
														: 'border-gray-300'
												}`}
											>
												{selectedPaymentMethod ===
													OrderPaymethods.payment_on_delivery && (
													<Check className='w-3 h-3 text-white' />
												)}
											</div>
											<Banknote className='w-6 h-6 text-green-600' />
											<div>
												<h3 className='font-semibold text-gray-900'>
													Pago contra entrega
												</h3>
												<p className='text-sm text-gray-600'>
													Paga en efectivo cuando recibas tu pedido
												</p>
											</div>
										</div>
									</div>

									<div className='border-2 border-gray-200 rounded-2xl p-4 opacity-50'>
										<div className='flex items-center gap-3'>
											<div className='w-5 h-5 rounded-full border-2 border-gray-300'></div>
											<CreditCard className='w-6 h-6 text-gray-400' />
											<div>
												<h3 className='font-semibold text-gray-500'>
													Pago en línea con Wompi
												</h3>
												<p className='text-sm text-gray-400'>
													Próximamente disponible
												</p>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='space-y-6'>
							<div className='bg-white rounded-3xl p-6 shadow-sm sticky top-8'>
								<div className='flex items-center gap-3 mb-6'>
									<div className='w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center'>
										<Package className='w-5 h-5 text-purple-600' />
									</div>
									<div>
										<h2 className='text-xl font-bold text-gray-900'>
											Resumen del Pedido
										</h2>
										<p className='text-gray-600 text-sm'>
											{finalData.totalItems} productos
										</p>
									</div>
								</div>

								<div className='space-y-4 mb-6 max-h-96 overflow-y-auto'>
									{items.map((item) => {
										const attribute = item.product.attributes?.[0]
										if (!attribute) return null

										const itemTotal = calculateItemTotal(item)
										const itemShipping = calculateItemShipping(item)
										const originalPrice = attribute.price || 0
										const discount = item.product.discount || 0
										const discountedPrice =
											originalPrice - (originalPrice * discount) / 100
										const totalAttributes = item.product.attributes?.length || 1
										const hasMultipleAttributes = totalAttributes > 1

										return (
											<div
												key={item._id}
												className='border border-gray-100 rounded-2xl p-4'
											>
												<div className='flex gap-3'>
													<div className='w-16 h-16 bg-gray-100 rounded-xl flex-shrink-0 overflow-hidden'>
														{attribute.image?.url ? (
															<img
																src={`${API_URL}/${attribute.image.url}`}
																alt={item.product.name}
																className='w-full h-full object-cover'
															/>
														) : item.product.images &&
														  item.product.images.length > 0 ? (
															<img
																src={`${API_URL}/${item.product.images[0].url}`}
																alt={item.product.name}
																className='w-full h-full object-cover'
															/>
														) : (
															<div className='w-full h-full flex items-center justify-center'>
																<ImageOff className='w-6 h-6 text-gray-400' />
															</div>
														)}
													</div>
													<div className='flex-1 min-w-0'>
														<h3 className='font-semibold text-gray-900 text-sm line-clamp-2 mb-1'>
															{item.product.name}
														</h3>
														<p className='text-xs text-gray-600 mb-2'>
															{item.product.brand}
														</p>

														<div className='flex items-center gap-2 mb-3 flex-wrap'>
															<span className='text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg font-medium'>
																{getAttributeDisplayValue(attribute)}
															</span>
															{discount > 0 && (
																<span className='text-xs bg-red-100 text-red-700 px-2 py-1 rounded-lg font-bold'>
																	-{discount}%
																</span>
															)}
															{hasMultipleAttributes && (
																<span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg font-medium'>
																	{totalAttributes} variantes
																</span>
															)}
														</div>

														<div className='flex items-center justify-between mb-3'>
															<span className='text-xs text-gray-500'>
																Cantidad: {attribute.quantity}
															</span>
														</div>

														<div className='flex items-end justify-between'>
															<div className='flex flex-col'>
																{discount > 0 ? (
																	<>
																		<span className='text-sm font-bold text-red-600'>
																			{formatCurrency(discountedPrice)}
																		</span>
																		<span className='text-xs text-gray-400 line-through'>
																			{formatCurrency(originalPrice)}
																		</span>
																	</>
																) : (
																	<span className='text-sm font-bold text-gray-900'>
																		{formatCurrency(originalPrice)}
																	</span>
																)}
																{itemShipping > 0 && (
																	<div className='flex items-center gap-1 mt-1'>
																		<Truck className='w-3 h-3 text-green-600' />
																		<span className='text-xs text-green-600 font-medium'>
																			+{formatCurrency(itemShipping)}
																			{hasMultipleAttributes && (
																				<span className='text-xs text-blue-600 ml-1'>
																					(+{(totalAttributes - 1) * 50}% envío)
																				</span>
																			)}
																		</span>
																	</div>
																)}
															</div>
															<div className='text-right'>
																<span className='text-sm font-bold text-gray-900'>
																	{formatCurrency(itemTotal)}
																</span>
																{itemShipping > 0 && (
																	<div className='text-xs text-gray-500 mt-0.5'>
																		Total:{' '}
																		{formatCurrency(itemTotal + itemShipping)}
																	</div>
																)}
															</div>
														</div>
													</div>
												</div>
											</div>
										)
									})}
								</div>

								<div className='border-t border-gray-200 pt-6 space-y-3'>
									<div className='flex justify-between text-sm text-gray-600'>
										<span>Subtotal ({finalData.totalItems} productos):</span>
										<span className='font-semibold'>
											{formatCurrency(finalData.totalPrice)}
										</span>
									</div>

									{coupon && finalData.couponDiscount > 0 && (
										<div className='flex justify-between text-sm text-green-600'>
											<span className='flex items-center gap-1'>
												<Gift className='w-3 h-3' />
												Descuento cupón:
											</span>
											<span className='font-semibold'>
												-{formatCurrency(finalData.couponDiscount)}
											</span>
										</div>
									)}

									<div className='flex justify-between text-sm'>
										<span className='flex items-center gap-1 text-gray-600'>
											<Truck className='w-4 h-4 text-green-600' />
											Envío:
										</span>
										<span className='font-semibold text-green-600'>
											{finalData.totalShipping > 0
												? formatCurrency(finalData.totalShipping)
												: 'GRATIS'}
										</span>
									</div>

									<div className='border-t border-gray-200 pt-3'>
										<div className='flex justify-between text-lg font-bold text-gray-900'>
											<span>Total a pagar:</span>
											<span className='text-red-600'>
												{formatCurrency(finalData.grandTotal)}
											</span>
										</div>
									</div>
								</div>

								<button
									onClick={handleCreateOrder}
									disabled={
										!selectedAddress || isProcessing || items.length === 0
									}
									className='w-full cursor-pointer mt-6 bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
								>
									{isProcessing ? (
										<>
											<Loader2 className='w-5 h-5 animate-spin' />
											Procesando...
										</>
									) : (
										<>
											<CreditCard className='w-5 h-5' />
											Confirmar pedido
										</>
									)}
								</button>

								<p className='text-xs text-gray-500 text-center mt-4'>
									Al confirmar tu pedido aceptas nuestros términos y condiciones
								</p>

								{!orders?.coupon && (
									<div className='bg-red-50 rounded-2xl border border-red-100 p-3 sm:p-4 mt-6'>
										<div className='flex items-center gap-2 mb-3'>
											<Gift className='w-4 h-4 text-red-600' />
											<h4 className='font-bold text-red-900 text-sm'>
												¿Tienes un cupón de descuento?
											</h4>
										</div>

										{coupon ? (
											<div className='bg-green-100 border border-green-200 rounded-xl p-3'>
												<div className='flex items-center justify-between'>
													<div className='flex items-center gap-2'>
														<Check className='w-4 h-4 text-green-600' />
														<span className='text-sm font-medium text-green-800'>
															Cupón aplicado
														</span>
													</div>
													<button
														onClick={handleRemoveCoupon}
														className='p-1 hover:bg-green-200 rounded-lg transition-colors'
													>
														<X className='w-4 h-4 text-green-600' />
													</button>
												</div>
												<div className='mt-2'>
													<p className='text-xs text-green-700 font-mono break-all'>
														{coupon.code}
													</p>
													<p className='text-xs text-green-600 mt-1'>
														{coupon.type === TypesCoupon.DISCOUNT
															? `${coupon.discount}% de descuento`
															: `${formatCurrency(
																	coupon.amount || 0
															  )} de descuento`}
													</p>
													{finalData.couponDiscount > 0 && (
														<p className='text-xs text-green-700 font-semibold mt-1'>
															Ahorro: {formatCurrency(finalData.couponDiscount)}
														</p>
													)}
												</div>
											</div>
										) : (
											<div className='flex flex-col xs:flex-row gap-2'>
												<input
													type='text'
													value={promoCode}
													onChange={(e) => setPromoCode(e.target.value)}
													placeholder='Código de cupón'
													className='flex-1 px-3 py-2 border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300 text-sm min-w-0'
													onKeyPress={(e) => {
														if (e.key === 'Enter') {
															handleApplyPromoCode()
														}
													}}
												/>
												<button
													onClick={handleApplyPromoCode}
													disabled={isCouponLoading || !promoCode.trim()}
													className='bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap min-w-fit'
												>
													{isCouponLoading ? (
														<Loader2 className='w-4 h-4 animate-spin' />
													) : (
														'Aplicar'
													)}
												</button>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	)
}

export default PaymentScreen
